import { ChevronDown, MessageCircle, Sparkles, Play } from "lucide-react";
import { HERO_BG, OWNER_IMG, whatsappUrl } from "../data/content";

// Decorative gold particles floating in hero
const Particles = () => {
    const dots = Array.from({ length: 28 }).map((_, i) => ({
        left: `${(i * 37) % 100}%`,
        top: `${(i * 53) % 100}%`,
        size: 2 + (i % 4),
        delay: (i % 8) * 0.6,
        dur: 6 + (i % 5),
    }));
    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {dots.map((d, i) => (
                <span
                    key={i}
                    className="absolute rounded-full bg-gold/70 blur-[1px]"
                    style={{
                        left: d.left,
                        top: d.top,
                        width: d.size,
                        height: d.size,
                        animation: `heroFloat ${d.dur}s ease-in-out ${d.delay}s infinite alternate`,
                        opacity: 0.6,
                    }}
                />
            ))}
            <style>{`
                @keyframes heroFloat {
                    0% { transform: translateY(0) translateX(0); opacity: 0.2; }
                    50% { opacity: 0.9; }
                    100% { transform: translateY(-40px) translateX(20px); opacity: 0.1; }
                }
                @keyframes heroRingRotate {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export const Hero = () => {
    const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

    return (
        <section
            id="inicio"
            data-testid="hero-section"
            className="relative flex min-h-[100svh] items-center overflow-hidden"
        >
            {/* SEO hidden H1 */}
            <h1 className="sr-only">
                Mariachi Mexicanísimo — Serenatas y mariachis profesionales en Armenia, Quindío y el Eje Cafetero
            </h1>

            {/* Mariachi-themed background — desenfocada y oscurecida un poco más
                para que quede "atrás": así el retrato y el texto en primer
                plano contrastan mejor y se leen con más fuerza. */}
            <div
                className="absolute inset-0 overflow-hidden"
            >
                <div
                    className="absolute inset-[-4%]"
                    style={{
                        backgroundImage: `url(${HERO_BG})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        filter: "blur(3px) brightness(0.55) saturate(0.9)",
                    }}
                />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-background" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_50%,_transparent_0%,_rgba(0,0,0,0.82)_80%)]" />
            <div className="absolute inset-0 grain" />

            <Particles />

            {/* Gold corner frames */}
            <div className="pointer-events-none absolute left-0 top-0 h-40 w-40 border-l border-t border-gold/60 md:h-64 md:w-64" />
            <div className="pointer-events-none absolute right-0 bottom-0 h-40 w-40 border-r border-b border-gold/60 md:h-64 md:w-64" />

            {/* Split content */}
            <div className="relative z-10 mx-auto w-full max-w-7xl px-6 py-24 md:px-10 md:py-28">
                <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12 lg:gap-16">
                    {/* LEFT — portrait */}
                    <div className="fade-up order-2 flex justify-center lg:order-1 lg:col-span-6 lg:justify-start">
                        <div className="relative">
                            {/* Outer rotating dashed ring */}
                            <div
                                className="absolute inset-[-24px] rounded-full border border-dashed border-gold/40"
                                style={{ animation: "heroRingRotate 60s linear infinite" }}
                            />
                            <div className="absolute inset-[-14px] rounded-full border border-gold/60" />
                            {/* Glow */}
                            <div className="absolute inset-[-50px] rounded-full bg-gold/25 blur-3xl" />

                            {/* The circle */}
                            <div className="relative h-[280px] w-[280px] overflow-hidden rounded-full border-[3px] border-gold shadow-[0_0_80px_hsl(var(--gold)/0.45)] sm:h-[360px] sm:w-[360px] md:h-[440px] md:w-[440px]">
                                <img
                                    src={OWNER_IMG}
                                    alt="Mariachi Mexicanísimo"
                                    className="h-full w-full object-cover object-top"
                                    loading="eager"
                                />
                                <div className="absolute inset-0 rounded-full shadow-[inset_0_0_80px_rgba(0,0,0,0.6)]" />
                            </div>

                            {/* Floating sparkles */}
                            <div className="pointer-events-none absolute -right-2 -top-4 animate-float">
                                <Sparkles className="h-7 w-7 text-gold" />
                            </div>
                            <div
                                className="pointer-events-none absolute -left-6 bottom-4 animate-float"
                                style={{ animationDelay: "1.5s" }}
                            >
                                <Sparkles className="h-5 w-5 text-gold/70" />
                            </div>

                            {/* Floating credential badge (bottom right of portrait) */}
                            <div className="absolute -right-2 bottom-6 rounded-full border border-gold bg-black/80 px-4 py-2 backdrop-blur-md md:-right-4 md:bottom-10">
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gold" />
                                    <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold">
                                        +2000 Serenatas
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT — content */}
                    <div className="order-1 lg:order-2 lg:col-span-6">
                        <div className="fade-up max-w-xl">
                            <div className="inline-flex items-center gap-3 rounded-full border border-gold/50 bg-black/50 px-5 py-2 backdrop-blur">
                                <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
                                <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-gold">
                                    Armenia · Quindío · Eje Cafetero
                                </span>
                            </div>

                            <h2 className="mt-8 font-display text-5xl leading-[1.02] text-white sm:text-6xl md:text-7xl lg:text-[5.25rem]">
                                Serenatas
                                <span className="block text-gold">inolvidables.</span>
                            </h2>

                            <div className="mt-6 h-px w-24 bg-gold" />

                            <p className="mt-6 max-w-lg text-base leading-relaxed text-white/80 md:text-lg">
                                Mariachis profesionales en Armenia y todo el Eje Cafetero.
                                Cumpleaños, aniversarios, serenatas románticas y eventos especiales
                                con pasión mexicana — disponibles 24/7.
                            </p>

                            <div className="mt-10 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                                <a
                                    href={whatsappUrl()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="hero-cta-whatsapp"
                                    className="inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-bold uppercase tracking-[0.2em] text-black transition hover:scale-[1.03] hover:shadow-[0_0_40px_hsl(var(--gold)/0.55)]"
                                >
                                    <MessageCircle className="h-4 w-4" />
                                    Reservar
                                </a>
                                <button
                                    onClick={() => go("nosotros")}
                                    data-testid="hero-cta-video"
                                    className="group inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-4 text-sm font-bold uppercase tracking-[0.2em] text-white backdrop-blur-sm transition hover:border-gold hover:text-gold"
                                >
                                    <span className="flex h-6 w-6 items-center justify-center rounded-full border border-gold text-gold">
                                        <Play className="h-3 w-3 translate-x-0.5" fill="currentColor" />
                                    </span>
                                    Ver video
                                </button>
                            </div>

                            {/* Micro stats */}
                            <div className="mt-12 flex items-center gap-8 border-t border-white/15 pt-6">
                                {[
                                    { k: "+2000", v: "Serenatas" },
                                    { k: "6", v: "Músicos" },
                                    { k: "24/7", v: "Disponibles" },
                                ].map((s) => (
                                    <div key={s.v}>
                                        <div className="font-display text-2xl text-gold md:text-3xl">
                                            {s.k}
                                        </div>
                                        <div className="mt-0.5 text-[9px] uppercase tracking-[0.25em] text-white/60">
                                            {s.v}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scroll indicator */}
            <button
                onClick={() => go("nosotros")}
                className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-white/70 transition hover:text-gold"
                aria-label="Desplazar"
                data-testid="hero-scroll-indicator"
            >
                <ChevronDown className="h-8 w-8 animate-bounce" />
            </button>
        </section>
    );
};

export default Hero;
